import { getLogoutUseCase } from '@/di/auth.container';
import type {
  HttpErrorContext,
  HttpErrorType,
} from '@/domains/http/httpError.entity';
import { HttpErrorType as ErrorType } from '@/domains/http/httpError.entity';
import type { HttpErrorHandlerRepository } from '@/domains/http/httpErrorHandler.repository';
import { errorNotificationService } from './errorNotification.service';
import { warningNotificationService } from './warningNotification.service';

/**
 * Servicio de manejo centralizado de errores HTTP con categorización y respuestas específicas
 * Cumple con SRP: responsabilidad única de procesar y delegar errores HTTP
 */
class HttpErrorHandlerService implements HttpErrorHandlerRepository {
  /**
   * Maneja error HTTP categorizándolo y ejecutando acción correspondiente
   * @param context - Contexto del error con status, URL y metadatos
   */
  async handleError(context: HttpErrorContext): Promise<void> {
    const errorType = this.categorizeError(context);

    switch (errorType) {
      case ErrorType.UNAUTHORIZED:
        await this.handleUnauthorized(context);
        break;
      case ErrorType.FORBIDDEN:
        await this.handleForbidden();
        break;
      case ErrorType.NOT_FOUND:
        await this.handleNotFound(context);
        break;
      case ErrorType.SERVER_ERROR:
        await this.handleServerError();
        break;
      case ErrorType.NETWORK_ERROR:
        await this.handleNetworkError();
        break;
      case ErrorType.SERVICE_UNAVAILABLE:
        await this.handleServiceUnavailable();
        break;
    }
  }

  /**
   * Categoriza el error basado en el código de estado HTTP
   * @param context - Contexto del error con código de estado
   */
  private categorizeError(context: HttpErrorContext): HttpErrorType {
    switch (context.status) {
      case 401:
        return ErrorType.UNAUTHORIZED;
      case 403:
        return ErrorType.FORBIDDEN;
      case 404:
        return ErrorType.NOT_FOUND;
      case 500:
        return ErrorType.SERVER_ERROR;
      case 503:
        return ErrorType.SERVICE_UNAVAILABLE;
      case 0:
        return ErrorType.NETWORK_ERROR;
      default:
        return ErrorType.SERVER_ERROR;
    }
  }

  /**
   * Maneja errores de autorización con logout automático excepto en login
   * @param context - Contexto del error para verificar si es request de login
   */
  private async handleUnauthorized(context: HttpErrorContext): Promise<void> {
    if (context.isLoginRequest) {
      await errorNotificationService.showError('Credenciales incorrectas');
    } else {
      await warningNotificationService.showWarning('Tu sesiónha expirado');
      await getLogoutUseCase().execute();
    }
  }

  /**
   * Maneja errores de permisos insuficientes mostrando mensaje al usuario
   */
  private async handleForbidden(): Promise<void> {
    await errorNotificationService.showError(
      'No tienes permisos suficientes para acceder a este recurso',
    );
  }

  /**
   * Maneja errores de recurso no encontrado con nombre extraído de URL
   * @param context - Contexto del error con URL del recurso
   */
  private async handleNotFound(context: HttpErrorContext): Promise<void> {
    const resourceName = this.extractResourceNameFromUrl(context.url);
    await errorNotificationService.showError(
      `Recurso no encontrado: ${resourceName}`,
    );
  }

  /**
   * Maneja errores internos del servidor mostrando mensaje genérico
   */
  private async handleServerError(): Promise<void> {
    await errorNotificationService.showError(
      'Error interno del servidor. Por favor, inténtalo más tarde.',
    );
  }

  /**
   * Maneja errores de conectividad de red
   */
  private async handleNetworkError(): Promise<void> {
    await errorNotificationService.showError(
      'Error de conexion. Intenta más tarde.',
    );
  }

  /**
   * Maneja errores de servicio temporalmente no disponible
   */
  private async handleServiceUnavailable(): Promise<void> {
    await errorNotificationService.showError(
      'Servicio temporalmente no disponible. Inténtalo en unos minutos.',
    );
  }

  /**
   * Extrae nombre amigable del recurso desde la última parte de la URL
   * @param url - URL del recurso solicitado
   */
  private extractResourceNameFromUrl(url?: string): string {
    if (!url) return 'Recurso solicitado';

    const segments = url.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];

    return lastSegment || 'Recurso solicitado';
  }
}

export const httpErrorHandlerService = new HttpErrorHandlerService();
